import L from 'leaflet';
import 'leaflet.markercluster';

const getClusterConfig = (featureCount) => {
  /**
   * Get clustering configuration based on feature count
   * Optimizes performance for different data densities
   * 
   * @param {number} featureCount - Number of features to cluster
   * @returns {Object} Cluster configuration
   * @returns {number} returns.maxClusterRadius - Maximum radius for clustering (50-80px)
   * @returns {number} returns.chunkInterval - Processing interval in ms (100-400ms)
   * @returns {number} returns.disableClusteringAtZoom - Zoom level to stop clustering (12-16)
   */

  if (featureCount > 10000) {
    return {
      maxClusterRadius: 80,
      chunkInterval: 100,
      disableClusteringAtZoom: 16,
    };
  } else if (featureCount > 1000) {
    return {
      maxClusterRadius: 60,
      chunkInterval: 200,
      disableClusteringAtZoom: 13,
    };
  } else {
    return {
      maxClusterRadius: 50,
      chunkInterval: 400,
      disableClusteringAtZoom: 12,
    };
  }
};

export const safeClusterOperation = (operation, layerKey, onError = null) => {
  /**
   * Safe cluster operation wrapper
   * Handles errors gracefully during cluster operations
   * 
   * @param {Function} operation - Operation to perform
   * @param {string} layerKey - Layer identifier for error logging
   * @param {Function} onError - Optional error handler
   * @returns {*} Result of operation or error handler result
   */

  try {
    return operation();
  } catch (error) {
    console.error(`Clustering error in layer ${layerKey}:`, error);

    if (
      error.message?.includes('getMinZoom') ||
      error.message?.includes('null')
    ) {
      console.warn(
        `Map instance issue detected for ${layerKey}. Attempting recovery...`
      );

      if (onError) {
        return onError();
      } else {
        return null;
      }
    }

    throw error;
  }
};

export const createMarkerClusterGroup = (map, layerKey, featureCount) => {
  /**
   * Create marker cluster group with optimized settings
   * 
   * @param {Object} map - Leaflet map instance
   * @param {string} layerKey - Layer identifier
   * @param {number} featureCount - Number of features
   * @returns {L.MarkerClusterGroup|null} Cluster group or null if failed
   */

  return safeClusterOperation(
    () => {
      /**
       * Validate map instance before creating cluster
       */
      if (!map || !map.getContainer()) {
        throw new Error('Map instance not valid');
      }

      const config = getClusterConfig(featureCount);

      const clusterGroup = L.markerClusterGroup({
        chunkedLoading: true,
        chunkInterval: config.chunkInterval,
        maxClusterRadius: config.maxClusterRadius,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: config.disableClusteringAtZoom,

        iconCreateFunction: (cluster) => {
          /**
           * Create cluster icon with count badge
           * 
           * @param {L.MarkerCluster} cluster - Cluster object
           * @returns {L.DivIcon} Custom cluster icon
           */
          try {
            const count = cluster.getChildCount();
            let className = 'marker-cluster-small';

            if (count > 100) className = 'marker-cluster-large';
            else if (count > 10) className = 'marker-cluster-medium';

            return L.divIcon({
              html: `<div><span>${count}</span></div>`,
              className: `marker-cluster ${className}`,
              iconSize: [40, 40],
            });
          } catch (error) {
            console.warn('Error creating cluster icon:', error);
            return L.divIcon({
              html: `<div><span>?</span></div>`,
              className: 'marker-cluster marker-cluster-small',
              iconSize: [40, 40],
            });
          }
        },
      });

      return clusterGroup;
    },
    layerKey
  );
};

export const safeAddToCluster = (clusterGroup, marker, map, layerKey) => {
  /**
   * Safely add marker to cluster group
   * 
   * @param {L.MarkerClusterGroup} clusterGroup - Cluster group
   * @param {L.Marker} marker - Marker to add
   * @param {Object} map - Leaflet map instance
   * @param {string} layerKey - Layer identifier
   * @returns {boolean} True if successful, false otherwise
   */

  return safeClusterOperation(
    () => {
      /**
       * Validate cluster group and map before adding marker
       */
      if (!clusterGroup || !map || !map.getContainer()) {
        throw new Error('Invalid cluster group or map instance');
      }
      clusterGroup.addLayer(marker);
      return true;
    },
    layerKey,
    () => {
      console.warn(
        `Failed to add marker to cluster in ${layerKey}. Skipping marker.`
      );
      return false;
    }
  );
};

export const safeAddToMap = (map, layer, layerKey) => {
  /**
   * Safely add layer to map
   * 
   * @param {Object} map - Leaflet map instance
   * @param {L.Layer} layer - Layer to add
   * @param {string} layerKey - Layer identifier
   * @returns {boolean} True if successful, false otherwise
   */
  
  return safeClusterOperation(
    () => {
      /**
       * Validate map instance before adding layer
       */
      if (!map || !map.getContainer()) {
        throw new Error('Map instance not valid');
      }
      map.addLayer(layer);
      return true;
    },
    layerKey,
    () => {
      console.warn(`Failed to add layer ${layerKey} to map. Skipping layer.`);
      return false;
    }
  );
};